import { Radio } from 'antd';
import { DarwinTable, DTableColumnsType } from 'components/darwin-ui';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AssignedPermission, PermissionModules } from 'types/rolesTypes';

interface DataType {
  key: string;
  module: PermissionModules;
  read: boolean;
  update: boolean;
  create: boolean;
  delete: boolean;
}

type ActionKey = 'read' | 'update' | 'create' | 'delete';

interface RolePermissionsTableProps {
  permissions: AssignedPermission[];
  title?: string;
}

export default function RolePermissionsTable({
  permissions,
  title,
}: RolePermissionsTableProps) {
  const { t } = useTranslation();

  const moduleKeyMap: Record<PermissionModules, string> = {
    [PermissionModules.Organizations]: 'organizations',
    [PermissionModules.Users]: 'users',
    [PermissionModules.Products]: 'products',
    [PermissionModules.DataTables]: 'data-tables',
    [PermissionModules.DataSources]: 'data-sources',
    [PermissionModules.Integrations]: 'integrations',
    [PermissionModules.Forms]: 'forms',
    [PermissionModules.LandingPages]: 'landing-pages',
    [PermissionModules.SiteManagement]: 'site-management',
    [PermissionModules.ProcessMaps]: 'process-maps',
    [PermissionModules.RoleManagement]: 'role-management',
    [PermissionModules.FormsDataCapture]: 'forms-data-capture',
    [PermissionModules.RegistryManagement]: 'registry-management',
    [PermissionModules.FlatFileUpload]: 'flat-file-upload',
    [PermissionModules.BlockchainLogging]: 'blockchain-logging',
    [PermissionModules.ReportingAndAnalytics]: 'reporting-and-analytics',
    [PermissionModules.Billing]: 'billing',
    [PermissionModules.Subscriptions]: 'subscriptions',
  };

  const getModuleKey = (moduleName: PermissionModules): string => {
    return moduleKeyMap[moduleName] || moduleName;
  };

  const columns: DTableColumnsType<DataType> = [
    {
      title: t('module'),
      dataIndex: 'module',
      key: 'module',
      width: '200px',
      render: (module: PermissionModules) =>
        t(
          `roles.wizard-sections.permission-section.permissions-list.${getModuleKey(
            module
          )}`
        ),
    },
    {
      title: t('read'),
      dataIndex: 'read',
      key: 'read',
      align: 'center',
      render: (value: boolean) => <Radio disabled checked={value} />,
    },
    {
      title: t('update'),
      dataIndex: 'update',
      key: 'update',
      align: 'center',
      render: (value: boolean) => <Radio disabled checked={value} />,
    },
    {
      title: t('create'),
      dataIndex: 'create',
      key: 'create',
      align: 'center',
      render: (value: boolean) => <Radio disabled checked={value} />,
    },
    {
      title: t('delete'),
      dataIndex: 'delete',
      key: 'delete',
      align: 'center',
      render: (value: boolean) => <Radio disabled checked={value} />,
    },
  ];

  const data = useMemo(() => {
    const modulePermissionsMap: Record<string, DataType> = {};

    permissions.forEach(item => {
      const moduleName = item.permission.module;
      const actionStr = item.permission.action.toLowerCase();
      const granted = item.granted;

      if (!modulePermissionsMap[moduleName]) {
        modulePermissionsMap[moduleName] = {
          key: moduleName,
          module: moduleName,
          read: false,
          update: false,
          create: false,
          delete: false,
        };
      }

      if (['read', 'update', 'create', 'delete'].includes(actionStr)) {
        const action = actionStr as ActionKey;
        modulePermissionsMap[moduleName][action] = granted;
      }
    });

    return Object.values(modulePermissionsMap);
  }, [permissions]);

  return (
    <DarwinTable
      columns={columns}
      dataSource={data}
      pagination={false}
      bordered
      scroll={{ x: true }}
    />
  );
}
